import React, {
  memo, useCallback, useEffect, useState, useRef,
} from 'react';
import isNil from 'lodash/isNil';
import isEmpty from 'lodash/isEmpty';
import Form, { FormConsumer } from '@active/react-ui/Form';
import { useSelector, useDispatch } from 'react-redux';
import find from 'lodash/find';
import debounce from 'lodash/throttle';
import PaymentOptionsContent from './components/PaymentOptionsContent';
import {
  setCurrentPaymentOptionType,
  setSupportPaymentMethods,
  setPayManualAndPayInfull,
  setPaymentOptionData,
  setIsRenderPaymentOptions,
  setUseCreditBalance,
  setCreditBalance,
  setAmountDue,
  setUpdateBillingSchedule,
  setPaymentPlanOptions,
  setPaymentProcessingFee,
  setIsPaymentProcessingFeeModel,
  setPaymentPlanIdAndGroups,
  setFuturePaymentProcessingFeeAmount,
} from './paymentOptionsSlice';
import { isOnlyPayInfullWithoutSubOptions, matchPaymentCombination } from './utils/commonUtils';
import {
  useGetPaymentOptionsQuery,
  useLazyCalculatePPFQuery,
} from '../../../../../store/services/common/payment/paymentApi';
import pubSubInstance from '../../../../../shared/utils/pubSub';
import { objectFilter } from '../../../../../shared/utils/commonUtils';

const PaymentOptions = () => {
  const paymentForm = useRef();
  const dispatch = useDispatch();
  const {
    isHidePaymentOptionsORT,
    widgetNameSpace,
  } = useSelector(state => state.propsData);
  const {
    entities: {
      orderInfo: {
        thirdPartyAmount,
      } = {},
    },
    defaultData: {
      paymentOptionType: defaultPaymentOptionType,
      updateBillingSchedule: defaultUpdateBillingSchedule,
      useCreditBalance: defaultUseCreditBalance,
      customAmount: defaultCustomAmount,
    } = {},
  } = useSelector(state => state.mainStateData);

  const [ isInitialization, setIsInitialization ] = useState(true);
  const {
    currentPaymentOptionType,
    isRenderPaymentOptions,
    useCreditBalance,
    updateBillingSchedule,
    paymentPlanOptions,
    isPayManual,
    isPayInfull,
    amountDue,
    isPaymentProcessingFeeModel,
  } = useSelector(state => state.paymentOptions);
  const {
    currentActivePaymentMethod,
  } = useSelector(state => state.paymentMethods);

  const { data: paymentOptionData, isSuccess: isGetOptionsSuccess, refetch } = useGetPaymentOptionsQuery();
  const [ calculatePPF ] = useLazyCalculatePPFQuery();

  useEffect(() => {
    if (isGetOptionsSuccess) {
      const { paymentCombinationGroups } = paymentOptionData;
      if (paymentCombinationGroups) {
        dispatch(setIsPaymentProcessingFeeModel(true));
      } else {
        dispatch(setIsPaymentProcessingFeeModel(false));
      }
      if (paymentCombinationGroups && currentPaymentOptionType && currentActivePaymentMethod) {
        const {
          amount: paymentProcessingFee, paymentPlanIdAndGroups, futurePaymentProcessingFeeAmount,
        } = matchPaymentCombination(paymentCombinationGroups, {
          paymentOptionType: currentPaymentOptionType,
          paymentMethodType: currentActivePaymentMethod,
          isUseCreditBalance: useCreditBalance,
          paymentPlanOptions,
        }) || {};
        dispatch(setPaymentPlanIdAndGroups(paymentPlanIdAndGroups || []));
        dispatch(setPaymentProcessingFee(isNil(paymentProcessingFee) ? 0 : paymentProcessingFee));
        dispatch(setFuturePaymentProcessingFeeAmount(
          isNil(futurePaymentProcessingFeeAmount) ? 0 : futurePaymentProcessingFeeAmount));
      } else {
        dispatch(setPaymentProcessingFee(0));
        dispatch(setPaymentPlanIdAndGroups([]));
        dispatch(setFuturePaymentProcessingFeeAmount(0));
      }
    }
  }, [ isGetOptionsSuccess, paymentOptionData, currentActivePaymentMethod,
    currentPaymentOptionType, useCreditBalance, paymentPlanOptions, dispatch ]);

  useEffect(() => {
    if (isGetOptionsSuccess) {
      const { paymentOptions } = paymentOptionData;
      if (paymentOptions.length === 0
        || (isHidePaymentOptionsORT && isOnlyPayInfullWithoutSubOptions(paymentOptions))) {
        dispatch(setIsRenderPaymentOptions(false));
      } else {
        dispatch(setIsRenderPaymentOptions(true));
      }
    }
  }, [ isHidePaymentOptionsORT, isGetOptionsSuccess, paymentOptionData, dispatch ]);

  useEffect(debounce(() => { // reset supportPaymentMethods for states changes
    if (isGetOptionsSuccess && currentPaymentOptionType) {
      dispatch(setSupportPaymentMethods({ thirdPartyAmount }));
    }
  }, 200, { leading: false, trailing: true }),
  [ paymentOptionData, paymentPlanOptions, isPayManual, isPayInfull, useCreditBalance, thirdPartyAmount,
    updateBillingSchedule, currentPaymentOptionType, dispatch, isGetOptionsSuccess ]);

  const resetStateForPaymentOptionChanged = (type) => {
    if (type) {
      const { paymentOptions } = paymentOptionData;
      const { subOptions = [], amount } = find(paymentOptions, { type }) || {};
      const creditBalanceOption = find(subOptions, { type: 'APPLY_CREDIT_BALANCE' }) || {};
      const { creditBalance } = creditBalanceOption;
      dispatch(setCreditBalance(creditBalance));
      dispatch(setAmountDue(!isNil(amount) ? amount : undefined)); // payment plan case have no amount

      if (isInitialization) {
        dispatch(setUseCreditBalance(!!defaultUseCreditBalance));
        dispatch(setUpdateBillingSchedule(!!defaultUpdateBillingSchedule));
        type === 'PAY_CUSTOM_AMOUNT' && defaultCustomAmount && dispatch(setAmountDue(defaultCustomAmount));
        setIsInitialization(false);
      } else {
        dispatch(setUseCreditBalance(false));
        dispatch(setUpdateBillingSchedule(false));
        dispatch(setPayManualAndPayInfull());
        type !== 'PAY_DEPOSIT_WITH_PAYMENT_PLAN' && dispatch(setPaymentPlanOptions([]));
        paymentForm.current?.setValues({
          paymentOptionType: type,
          useCreditBalance: [],
          updateBillingSchedule: [],
          customAmount: '',
        });
      }
    }
  };

  useEffect(() => {
    if (isGetOptionsSuccess) {
      dispatch(setPaymentOptionData(paymentOptionData));
    }
  }, [ isGetOptionsSuccess, paymentOptionData ]);

  useEffect(() => {
    if (isGetOptionsSuccess) {
      const { paymentOptions } = paymentOptionData;
      const currentPaymentType = defaultPaymentOptionType
      && !!find(paymentOptions, { type: defaultPaymentOptionType })
        ? defaultPaymentOptionType : paymentOptions[0]?.type;
      dispatch(setCurrentPaymentOptionType(currentPaymentType));
      resetStateForPaymentOptionChanged(currentPaymentType);
    }
  }, [ isGetOptionsSuccess, paymentOptionData, defaultPaymentOptionType ]);

  useEffect(() => {
    if (isPaymentProcessingFeeModel
      && amountDue > 0
      && currentPaymentOptionType === 'PAY_CUSTOM_AMOUNT') {
      calculatePPF({ params: { amount: amountDue } }, { preferCacheValue: true }).unwrap().then((res) => {
        const paymentProcessingFee = matchPaymentCombination(res.paymentCombinationGroups, {
          paymentOptionType: currentPaymentOptionType,
          paymentMethodType: currentActivePaymentMethod,
          isUseCreditBalance: useCreditBalance,
        })?.amount;
        dispatch(setPaymentProcessingFee(isNil(paymentProcessingFee) ? 0 : paymentProcessingFee));
      }).catch();
    }
  }, [ calculatePPF, currentActivePaymentMethod, currentPaymentOptionType, amountDue,
    useCreditBalance, isPaymentProcessingFeeModel ]);

  const handleTypeChange = useCallback((e) => {
    const paymentOptionType = e.target.value;
    if (currentPaymentOptionType !== paymentOptionType) {
      dispatch(setCurrentPaymentOptionType(paymentOptionType));
      resetStateForPaymentOptionChanged(paymentOptionType);
    }
  }, [ dispatch, currentPaymentOptionType, resetStateForPaymentOptionChanged ]);

  const handleValidate = useCallback(() => {
    return new Promise((resolve) => {
      if (!isRenderPaymentOptions) {
        resolve(true);
      }

      paymentForm.current.validateForm({ shouldTouchFields: true }).then((res) => {
        const formValid = isEmpty(res);
        resolve(formValid);
      });
    });
  }, [ paymentForm, isRenderPaymentOptions ]);

  const handleSubmit = useCallback(() => {
    if (!isRenderPaymentOptions) {
      return Promise.resolve({
        status: true,
        paymentOptionType: 'PAY_REMAINING_BALANCE',
      });
    }
    const {
      useCreditBalance: useCreditBalanceVal,
      updateBillingSchedule: updateBillingScheduleval,
      paymentOptionType,
      customAmount,
    } = paymentForm.current.values;
    const isUseCreditBalance = !!useCreditBalanceVal?.[0];
    const isUpdateBillingScheduleVal = !!updateBillingScheduleval?.[0];
    const result = objectFilter({
      status: true,
      paymentOptionType,
      updateBillingSchedule: isUpdateBillingScheduleVal,
      useCreditBalance: isUseCreditBalance,
      customAmount,
      paymentPlanOptions: paymentPlanOptions.length > 0 ? paymentPlanOptions : undefined,
    });
    return Promise.resolve(result);
  }, [ paymentForm, paymentPlanOptions, isRenderPaymentOptions ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-validate`, handleValidate);
    pubSubInstance.subscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-validate`, handleValidate);
      pubSubInstance.unsubscribe(`${widgetNameSpace}-payment-item-submit`, handleSubmit);
    };
  }, [ widgetNameSpace, handleValidate, handleSubmit, isRenderPaymentOptions,
    currentPaymentOptionType, paymentPlanOptions ]);

  useEffect(() => {
    pubSubInstance.subscribe(`${widgetNameSpace}-reload`, refetch);
    return () => {
      pubSubInstance.unsubscribe(`${widgetNameSpace}-reload`, refetch);
    };
  }, [ refetch, widgetNameSpace ]);

  if (!isRenderPaymentOptions) {
    return null;
  }

  const formFields = {
    useCreditBalance: defaultUseCreditBalance ? [ 'true' ] : [],
    updateBillingSchedule: defaultUpdateBillingSchedule ? [ 'true' ] : [],
    customAmount: defaultCustomAmount || '',
    paymentOptionType: currentPaymentOptionType,
  };

  return (
    <Form customizeLayout
      initialValues={formFields}>
      <PaymentOptionsContent handleTypeChange={handleTypeChange} />
      <FormConsumer>
        {
          ({
            form,
          }) => {
            paymentForm.current = form;
          }
        }
      </FormConsumer>
    </Form>
  );
};

export default memo(PaymentOptions);
